import React, { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { LogIn, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import HawkamLogo from "@/components/HawkamLogo";

export default function AdminLogin() {
  const { user, login, loading } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const loc = useLocation();

  if (loading) return null;
  if (user && user.role === "admin") {
    const to = loc.state?.from?.pathname || "/admin";
    return <Navigate to={to} replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      nav("/admin", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div dir="ltr" className="min-h-screen grid grid-cols-1 md:grid-cols-2 bg-cream" data-testid="admin-login">
      <div className="hidden md:flex flex-col justify-between bg-navy text-cream p-12 relative overflow-hidden">
        <div className="relative z-10">
          <div className="flex items-center gap-3">
            <HawkamLogo size={72} variant="mark" />
            <div>
              <div className="text-2xl font-serif">HAWKAM</div>
              <div className="text-xs uppercase tracking-[0.3em] text-gold">Governance Console</div>
            </div>
          </div>
          <h2 className="mt-16 text-4xl leading-tight">Where governance meets craft.</h2>
          <p className="mt-6 text-cream/80 max-w-md">
            Secure access to Hawkam&apos;s admin console. Every action is written to the immutable audit log.
          </p>
        </div>
        <div className="relative z-10 flex items-center gap-2 text-cream/60 text-xs">
          <ShieldCheck size={14} className="text-gold" /> Encrypted · Audit-logged · GDPR
        </div>
        <div className="absolute inset-0 opacity-30" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, rgba(201,169,97,0.2), transparent 40%)" }} />
      </div>

      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-md card-elev p-10">
          <span className="eyebrow">Admin Login</span>
          <h1 className="mt-2 text-3xl text-navy">Sign in to console</h1>
          <span className="divider-gold" />
          <div className="space-y-5">
            <input
              data-testid="login-email"
              type="email"
              placeholder="Email"
              className="field-box"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <input
              data-testid="login-password"
              type="password"
              placeholder="Password"
              className="field-box"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          <button
            data-testid="login-submit"
            type="submit"
            disabled={busy}
            className="btn-gold w-full mt-8 justify-center disabled:opacity-40"
          >
            <LogIn size={16} /> {busy ? "..." : "Sign In"}
          </button>
          <div className="mt-4 text-center">
            <a
              href="/forgot-password"
              data-testid="login-forgot"
              className="text-xs text-navy/60 hover:text-gold underline decoration-gold underline-offset-4"
            >
              Forgot your password?
            </a>
          </div>
        </form>
      </div>
    </div>
  );
}
